if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      hide() {
        if (this.imageZoom && this.zoomImage) this.syncGalleryImage();
        this.stopImageZoom();
        super.hide();
      }

      show(opener) {
        this.imageZoom = !!opener.closest('product-info.hss-mobile-product');
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(`[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`)
          .forEach((element) => element.classList.remove('active'));
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        if (this.imageZoom && activeMedia.tagName === 'IMG') {
          this.startImageZoom(activeMedia);
          return;
        }
        this.imageZoom = false;
        activeMedia.scrollIntoView();
        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        if (activeMedia.nodeName === 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
      }

      startImageZoom(image) {
        this.classList.add('hss-image-zoom');
        this.stage = this.querySelector('[role="document"]');
        this.zoomImages = [...this.stage.querySelectorAll(':scope > img[data-media-id]')];
        this.imageSources = new Map(this.zoomImages.map((item) => [item, this.readImageSource(item)]));
        this.product = this.openedBy.closest('product-info');
        this.pointers = new Map();
        this.lastTap = null;
        this.lastTouch = 0;
        this.controlsState = null;
        this.setupZoomEvents();
        this.stage.scrollTop = this.stage.scrollLeft = 0;
        this.measureStage();
        this.activateImage(image, 1);
        this.zoomResize = new ResizeObserver(() => {
          if (!this.imageZoom) return;
          const previous = this.stageRect;
          this.measureStage();
          if (previous.width !== this.stageRect.width || previous.height !== this.stageRect.height) {
            this.fitImage(this.scale, true);
            if (this.pointers.size) this.gestureStart();
          }
        });
        this.zoomResize.observe(this.stage);
      }

      setupZoomEvents() {
        if (this.zoomEventsReady) return;
        this.zoomEventsReady = true;
        const dialog = this.querySelector('[role="dialog"]');
        const tools = document.createElement('div');
        tools.className = 'hss-image-zoom-tools';
        this.zoomButton = document.createElement('button');
        this.zoomButton.type = 'button';
        this.zoomButton.setAttribute('aria-label', 'Fit image');
        this.zoomButton.textContent = 'Fit';
        this.zoomButton.addEventListener('click', () => this.fitImage());
        this.zoomOutButton = document.createElement('button');
        this.zoomOutButton.type = 'button';
        this.zoomOutButton.textContent = '−';
        this.zoomOutButton.setAttribute('aria-label', 'Zoom out');
        this.zoomOutButton.addEventListener('click', () => this.zoomAt(Math.max(1, this.scale - 0.5), { x: 0, y: 0 }));
        this.zoomInButton = document.createElement('button');
        this.zoomInButton.type = 'button';
        this.zoomInButton.textContent = '+';
        this.zoomInButton.setAttribute('aria-label', 'Zoom in');
        this.zoomInButton.addEventListener('click', () => this.zoomAt(Math.min(4, this.scale + 0.5), { x: 0, y: 0 }));
        // Keep the theme's mouse pointerup handler from closing the image viewer.
        tools.addEventListener('pointerup', (event) => event.stopPropagation());
        tools.append(this.zoomOutButton, this.zoomButton, this.zoomInButton);
        dialog.append(tools);
        this.imageNav = document.createElement('div');
        this.imageNav.className = 'hss-image-zoom-nav';
        this.imageNav.addEventListener('pointerup', (event) => event.stopPropagation());
        for (const [direction, label, symbol] of [[-1, 'Previous image', '‹'], [1, 'Next image', '›']]) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = symbol;
          button.setAttribute('aria-label', label);
          button.className = direction < 0 ? 'hss-zoom-prev' : 'hss-zoom-next';
          button.addEventListener('click', () => this.changeImage(direction));
          this.imageNav.append(button);
        }
        dialog.append(this.imageNav);
        this.imageCounter = document.createElement('span');
        this.imageCounter.className = 'hss-image-zoom-counter';
        this.imageCounter.setAttribute('role', 'status');
        this.imageCounter.setAttribute('aria-live', 'polite');
        dialog.append(this.imageCounter);
        this.zoomHint = document.createElement('p');
        this.zoomHint.className = 'hss-image-zoom-hint';
        dialog.append(this.zoomHint);
        this.loadStatus = document.createElement('div');
        this.loadStatus.className = 'hss-image-zoom-loading';
        this.loadStatus.hidden = true;
        this.loadMessage = document.createElement('span');
        this.loadMessage.setAttribute('role', 'status');
        this.loadRetry = document.createElement('button');
        this.loadRetry.type = 'button';
        this.loadRetry.textContent = 'Retry';
        this.loadRetry.addEventListener('click', () => {
          this.imageSources.get(this.zoomImage).failedUrl = null;
          this.ensureActiveImage();
        });
        this.loadStatus.addEventListener('pointerup', (event) => event.stopPropagation());
        this.loadStatus.append(this.loadMessage, this.loadRetry);
        dialog.append(this.loadStatus);
        this.stage.addEventListener('dragstart', (event) => { if (this.imageZoom) event.preventDefault(); });
        this.stage.addEventListener('pointerdown', (event) => this.zoomPointerDown(event));
        this.stage.addEventListener('pointermove', (event) => this.zoomPointerMove(event));
        this.stage.addEventListener('pointerup', (event) => this.zoomPointerEnd(event));
        this.stage.addEventListener('pointercancel', (event) => this.zoomPointerEnd(event));
        this.stage.addEventListener('lostpointercapture', (event) => this.zoomPointerEnd(event));
        this.stage.addEventListener('dblclick', (event) => {
          if (!this.imageZoom) return;
          event.preventDefault();
          if (Date.now() - this.lastTouch > 600) this.toggleZoom(this.zoomPoint(event));
        });
        dialog.addEventListener('keydown', (event) => {
          if (!this.imageZoom) return;
          if (event.key === '+' || event.key === '=') this.zoomAt(Math.min(4, this.scale + 0.5), { x: 0, y: 0 });
          else if (event.key === '-') this.zoomAt(Math.max(1, this.scale - 0.5), { x: 0, y: 0 });
          else if (event.key === '0') this.fitImage();
          else if (event.key === 'ArrowLeft') this.changeImage(-1);
          else if (event.key === 'ArrowRight') this.changeImage(1);
          else return;
          event.preventDefault();
        });
      }

      zoomPoint(event) {
        const r = this.stageRect;
        return { x: event.clientX - r.left - r.width / 2, y: event.clientY - r.top - r.height / 2 };
      }

      measureStage() {
        const previous = this.stageRect;
        this.stageRect = this.stage.getBoundingClientRect();
        this.phoneViewer = matchMedia('(max-width: 749px)').matches;
        if (previous && this.pointers.size) {
          const dx = previous.left + previous.width / 2 - this.stageRect.left - this.stageRect.width / 2;
          const dy = previous.top + previous.height / 2 - this.stageRect.top - this.stageRect.height / 2;
          for (const point of this.pointers.values()) { point.x += dx; point.y += dy; }
        }
      }

      fittedSize(source) {
        const fit = Math.min(this.stageRect.width / source.width, this.stageRect.height / source.height, 1);
        return { width: source.width * fit, height: source.height * fit };
      }

      fitImage(scale = 1, preservePan = false) {
        if (!this.imageZoom || !this.zoomImage) return;
        const image = this.zoomImage;
        const size = this.fittedSize(this.imageSources.get(image));
        const ratio = this.fitWidth ? size.width / this.fitWidth : 1;
        this.fitWidth = size.width;
        this.fitHeight = size.height;
        image.style.width = `${this.fitWidth}px`;
        image.style.height = `${this.fitHeight}px`;
        this.scale = scale;
        if (preservePan) {
          this.panX *= ratio;
          this.panY *= ratio;
        } else {
          this.panX = this.panY = 0;
          const captured = [...this.pointers.keys()];
          this.pointers.clear();
          for (const id of captured) {
            if (this.stage.hasPointerCapture(id)) this.stage.releasePointerCapture(id);
          }
          this.lastTap = null;
        }
        this.renderZoom();
        this.ensureActiveImage();
      }

      renderZoom() {
        const limitX = Math.max(0, (this.fitWidth * this.scale - this.stageRect.width) / 2);
        const limitY = Math.max(0, (this.fitHeight * this.scale - this.stageRect.height) / 2);
        this.panX = Math.max(-limitX, Math.min(limitX, this.panX));
        this.panY = Math.max(-limitY, Math.min(limitY, this.panY));
        if (this.zoomFrame) return;
        this.zoomFrame = requestAnimationFrame(() => {
          this.zoomFrame = null;
          if (!this.imageZoom) return;
          const transform = `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.scale})`;
          if (this.zoomImage.style.transform !== transform) this.zoomImage.style.transform = transform;
          this.updateZoomControls();
        });
      }

      updateZoomControls() {
        const state = [this.scale > 1.01, this.scale >= 4, this.pointers.size > 0, this.zoomImages.length > 1, this.phoneViewer].join(':');
        if (state === this.controlsState) return;
        this.controlsState = state;
        this.zoomOutButton.disabled = this.scale <= 1.01;
        this.zoomInButton.disabled = this.scale >= 4;
        this.stage.style.cursor = this.pointers.size ? 'grabbing' : 'grab';
        const multipleImages = this.zoomImages.length > 1;
        if (this.scale > 1.01) {
          this.zoomHint.textContent = multipleImages
            ? 'Drag to move. Swipe past an edge to change image.'
            : 'Drag to move. Use Fit to see the whole image.';
        } else {
          const browse = multipleImages ? 'Swipe or drag to browse. ' : '';
          this.zoomHint.textContent = browse + (this.phoneViewer
            ? 'Pinch or double-tap to zoom.' : 'Double-click to zoom.');
        }
      }

      zoomAt(scale, point) {
        const ratio = scale / this.scale;
        this.panX = point.x - (point.x - this.panX) * ratio;
        this.panY = point.y - (point.y - this.panY) * ratio;
        this.scale = scale;
        this.renderZoom();
        this.ensureActiveImage();
      }

      toggleZoom(point) {
        if (this.scale > 1.01) this.fitImage();
        else this.zoomAt(2.5, point);
      }

      gestureStart() {
        const points = [...this.pointers.values()];
        this.gesture = { scale: this.scale, x: this.panX, y: this.panY, first: points[0] };
        if (points.length >= 2) {
          this.gesture.distance = Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y));
          this.gesture.mid = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
        }
      }

      zoomPointerDown(event) {
        if (!this.imageZoom || (event.pointerType === 'mouse' && event.button !== 0)) return;
        if (!this.pointers.size) this.measureStage();
        const point = this.zoomPoint(event);
        if (!this.pointers.size) {
          this.tapStart = { ...point, time: Date.now() };
          this.gestureMoved = false;
          this.gestureHadMultiplePointers = false;
        } else {
          this.gestureMoved = true;
          this.gestureHadMultiplePointers = true;
          this.lastTap = null;
        }
        this.pointers.set(event.pointerId, point);
        this.stage.setPointerCapture(event.pointerId);
        this.gestureStart();
        this.renderZoom();
      }

      zoomPointerMove(event) {
        if (!this.imageZoom || !this.pointers.has(event.pointerId)) return;
        const point = this.zoomPoint(event);
        this.pointers.set(event.pointerId, point);
        if (Math.hypot(point.x - this.tapStart.x, point.y - this.tapStart.y) > 8) this.gestureMoved = true;
        const points = [...this.pointers.values()];
        const start = this.gesture;
        if (points.length >= 2) {
          const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
          this.scale = Math.max(1, Math.min(4, start.scale * distance / start.distance));
          const ratio = this.scale / start.scale;
          this.panX = (points[0].x + points[1].x) / 2 - (start.mid.x - start.x) * ratio;
          this.panY = (points[0].y + points[1].y) / 2 - (start.mid.y - start.y) * ratio;
        } else if (this.scale > 1) {
          this.panX = start.x + point.x - start.first.x;
          this.panY = start.y + point.y - start.first.y;
        }
        event.preventDefault();
        this.renderZoom();
      }

      zoomPointerEnd(event) {
        if (!this.imageZoom) return;
        event.stopPropagation();
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.delete(event.pointerId);
        if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
        const now = Date.now();
        if (event.pointerType !== 'mouse') this.lastTouch = now;
        const point = this.zoomPoint(event);
        const dx = point.x - this.gesture.first.x;
        const dy = point.y - this.gesture.first.y;
        const threshold = Math.max(40, Math.min(80, this.stageRect.width * 0.12));
        const limitX = Math.max(0, (this.fitWidth * this.scale - this.stageRect.width) / 2);
        const unboundedPanX = this.gesture.x + dx;
        const edgeDrag = unboundedPanX - Math.max(-limitX, Math.min(limitX, unboundedPanX));
        // A fitted image swipes immediately. A magnified image pans until its edge.
        const swiped = this.zoomImages.length > 1 && event.type === 'pointerup' && !this.pointers.size && !this.gestureHadMultiplePointers
          && Math.abs(dx) > Math.abs(dy) * 1.25 && Math.abs(edgeDrag) >= threshold;
        if (swiped) {
          this.lastTap = null;
          this.changeImage(edgeDrag < 0 ? 1 : -1);
          return;
        }
        if (event.type === 'pointerup' && event.pointerType !== 'mouse' && !this.gestureMoved && now - this.tapStart.time < 300) {
          if (this.lastTap && now - this.lastTap.time < 350 && Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) < 30) {
            this.toggleZoom(point);
            this.lastTap = null;
          } else this.lastTap = { ...point, time: now };
        } else this.lastTap = null;
        if (this.pointers.size) this.gestureStart();
        else this.ensureActiveImage();
        this.renderZoom();
      }

      updateImageCounter() {
        this.imageCounter.textContent = `${this.zoomImages.indexOf(this.zoomImage) + 1} / ${this.zoomImages.length}`;
        this.imageNav.hidden = this.zoomImages.length < 2;
      }

      changeImage(direction) {
        if (!this.imageZoom || this.zoomImages.length < 2) return;
        const index = (this.zoomImages.indexOf(this.zoomImage) + direction + this.zoomImages.length) % this.zoomImages.length;
        this.activateImage(this.zoomImages[index], this.scale);
      }

      activateImage(image, scale) {
        if (this.zoomImage) {
          this.zoomImage.classList.remove('active');
          for (const property of ['width', 'height', 'transform']) this.zoomImage.style.removeProperty(property);
        }
        this.zoomImage = image;
        const source = this.imageSources.get(image);
        source.touched = true;
        source.failedUrl = null;
        // The viewer controls resolution. Hidden originals remain lazy until selected.
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        if (source.ready) this.showImageSource(source, source.ready);
        if (!source.displayedWidth) {
          const button = this.product?.querySelector(`.product__media-toggle[data-media-id="${image.dataset.mediaId}"]`);
          const cached = button?.closest('.product__media-item')?.querySelector('.product__media img');
          if (cached?.complete && cached.naturalWidth && cached.currentSrc) {
            const width = Number(new URL(cached.currentSrc, document.baseURI).searchParams.get('width')) || source.width;
            this.showImageSource(source, { url: cached.currentSrc, width: Math.min(width, source.width) });
          } else {
            image.removeAttribute('src');
            image.style.visibility = 'hidden';
          }
        }
        this.zoomImage.classList.add('active');
        this.retainNearbyImages();
        this.fitImage(scale);
        this.updateImageCounter();
      }

      readImageSource(image) {
        const original = { src: image.getAttribute('src'), srcset: image.getAttribute('srcset'), sizes: image.getAttribute('sizes') };
        const candidates = (original.srcset || '').split(',').map((entry) => {
          const [url, descriptor] = entry.trim().split(/\s+/);
          return { url, width: parseInt(descriptor, 10) };
        }).filter((item) => item.url && item.width > 0).sort((a, b) => a.width - b.width);
        const width = Number(image.dataset.zoomWidth) || candidates.at(-1)?.width || Number(image.getAttribute('width'));
        const height = Number(image.dataset.zoomHeight) || width * Number(image.getAttribute('height')) / Number(image.getAttribute('width'));
        if (!candidates.length) candidates.push({ url: original.src, width });
        return { image, original, candidates, width, height, displayedWidth: 0 };
      }

      imageCandidate(source, scale = 1) {
        const required = Math.min(source.width, this.fittedSize(source).width * scale * (window.devicePixelRatio || 1));
        return source.candidates.find((item) => item.width >= required) || source.candidates.at(-1);
      }

      showImageSource(source, asset) {
        if (asset.width < source.displayedWidth || source.displayedUrl === asset.url) return;
        source.image.src = asset.url;
        source.image.style.visibility = '';
        source.displayedWidth = asset.width;
        source.displayedUrl = asset.url;
        // Dimensions and gesture state deliberately do not depend on image load/decode.
      }

      setLoadingState(message = '', failed = false) {
        const state = `${message}:${failed}`;
        if (state === this.loadingState) return;
        this.loadingState = state;
        this.loadStatus.hidden = !message;
        this.loadMessage.textContent = message;
        this.loadRetry.hidden = !failed;
      }

      ensureActiveImage() {
        if (!this.imageZoom || !this.imageSources) return;
        const source = this.imageSources.get(this.zoomImage);
        const candidate = this.imageCandidate(source, this.scale);
        if (source.ready && source.ready.width >= source.displayedWidth) this.showImageSource(source, source.ready);
        if (source.displayedWidth >= candidate.width) {
          this.setLoadingState();
          this.preloadNeighbours();
          return;
        }
        if (source.failedUrl === candidate.url) {
          this.setLoadingState(source.displayedWidth ? 'Could not load more detail.' : 'Could not load this photo.', true);
          return;
        }
        this.setLoadingState(source.displayedWidth ? 'Loading detail…' : 'Loading photo…');
        this.loadImageSource(source, candidate, 'high');
      }

      loadImageSource(source, candidate, priority = 'low') {
        if (source.displayedWidth >= candidate.width || source.ready?.width >= candidate.width || source.failedUrl === candidate.url) return;
        if (source.request?.width >= candidate.width) {
          if (priority === 'high') source.request.loader.fetchPriority = 'high';
          return;
        }
        source.request?.cancel();
        const loader = new Image();
        loader.decoding = 'async';
        loader.fetchPriority = priority;
        const request = { loader, width: candidate.width };
        source.request = request;
        let finished = false;
        const finish = (success, cancelled = false) => {
          if (finished) return;
          finished = true;
          clearTimeout(request.timeout);
          loader.onload = loader.onerror = null;
          if (source.request !== request) return;
          source.request = null;
          if (success) source.ready = { ...candidate, loader };
          else {
            loader.removeAttribute('src');
            if (!cancelled) source.failedUrl = candidate.url;
          }
          if (!cancelled && this.imageZoom && this.imageSources.get(this.zoomImage) === source) this.ensureActiveImage();
        };
        request.cancel = () => finish(false, true);
        request.timeout = setTimeout(() => finish(false), 15000);
        loader.onerror = () => finish(false);
        loader.onload = async () => {
          try { if (loader.decode) await loader.decode(); }
          catch { if (!loader.naturalWidth) { finish(false); return; } }
          finish(true);
        };
        loader.src = candidate.url;
      }

      nearbyImages() {
        const index = this.zoomImages.indexOf(this.zoomImage);
        const nearby = new Set([this.zoomImage]);
        if (!navigator.connection?.saveData && !/^(slow-)?2g$/.test(navigator.connection?.effectiveType || '')) {
          for (const step of [-1, 1]) nearby.add(this.zoomImages[(index + step + this.zoomImages.length) % this.zoomImages.length]);
        }
        return nearby;
      }

      retainNearbyImages() {
        const nearby = this.nearbyImages();
        for (const [image, source] of this.imageSources) {
          if (nearby.has(image)) continue;
          source.request?.cancel();
          source.ready = null;
          if (source.touched) {
            image.removeAttribute('src');
            source.displayedWidth = 0;
            source.displayedUrl = null;
          }
        }
      }

      preloadNeighbours() {
        for (const image of this.nearbyImages()) {
          if (image === this.zoomImage) continue;
          const source = this.imageSources.get(image);
          // Only fitted neighbours are prepared, never the whole full-resolution gallery.
          this.loadImageSource(source, this.imageCandidate(source));
        }
      }

      syncGalleryImage() {
        const product = this.openedBy?.closest('product-info.hss-mobile-product');
        const mediaId = this.zoomImage.getAttribute('data-media-id');
        const button = product?.querySelector(`.product__media-toggle[data-media-id="${mediaId}"]`);
        const slide = button?.closest('.product__media-item');
        if (!slide) return;
        const list = slide.parentElement;
        list.querySelectorAll('.product__media-item').forEach((item) => item.classList.toggle('is-active', item === slide));
        list.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
        const gallery = product.querySelector('media-gallery');
        gallery?.setActiveThumbnail(gallery.querySelector(`[data-target="${slide.dataset.mediaId}"]`));
        this.openedBy = button;
      }

      stopImageZoom() {
        this.imageZoom = false;
        this.zoomResize?.disconnect();
        cancelAnimationFrame(this.zoomFrame);
        this.zoomFrame = null;
        for (const [image, source] of this.imageSources || []) {
          source.request?.cancel();
          source.ready = null;
          if (!source.touched) continue;
          for (const property of ['width', 'height', 'transform', 'visibility']) image.style.removeProperty(property);
          for (const [name, value] of Object.entries(source.original)) {
            if (value !== null) image.setAttribute(name, value);
            else image.removeAttribute(name);
          }
        }
        this.imageSources = null;
        if (this.loadStatus) this.setLoadingState();
        this.pointers?.clear();
        this.stage?.style.removeProperty('cursor');
        this.classList.remove('hss-image-zoom');
        this.zoomImage = null;
      }
    }
  );
}
